import React, { useState } from "react";
import { Music2, Play, Pause, SkipForward, SkipBack } from "lucide-react";

// Placeholder track list — owner can swap in real Spotify/Apple Music data later
const TRACKS = [
  { id: 1, title: "Whiskey Glasses", artist: "Morgan Wallen", duration: "3:38" },
  { id: 2, title: "Friends in Low Places", artist: "Garth Brooks", duration: "4:34" },
  { id: 3, title: "Sweet Home Alabama", artist: "Lynyrd Skynyrd", duration: "4:43" },
  { id: 4, title: "Tennessee Whiskey", artist: "Chris Stapleton", duration: "4:53" },
  { id: 5, title: "Take It Easy", artist: "Eagles", duration: "3:32" },
  { id: 6, title: "Wagon Wheel", artist: "Old Crow Medicine Show", duration: "4:48" },
];

const Equalizer = ({ playing }) => (
  <div
    aria-hidden="true"
    className="flex items-end gap-1 h-5 w-7"
    data-testid="jukebox-equalizer"
  >
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="w-1 bg-[#FFB800] origin-bottom"
        style={{
          height: playing ? "100%" : "20%",
          animation: playing
            ? `eqBar ${0.7 + i * 0.15}s ease-in-out ${i * 0.1}s infinite alternate`
            : "none",
        }}
      />
    ))}
    <style>{`
      @keyframes eqBar {
        0% { transform: scaleY(0.25); }
        100% { transform: scaleY(1); }
      }
    `}</style>
  </div>
);

const TrackRow = ({ track, idx, current, onClick }) => {
  const isActive = idx === current;
  return (
    <li
      data-testid={`jukebox-track-${track.id}`}
      onClick={() => onClick(idx)}
      className={`grid grid-cols-[2.5rem_1fr_auto_auto] items-center gap-3 px-4 md:px-5 py-3 cursor-pointer transition-colors ${
        isActive
          ? "bg-[#1A1A1A] border-l-2 border-[#FF2A2A]"
          : "border-l-2 border-transparent hover:bg-[#1A1A1A]/60"
      }`}
    >
      <span
        className={`font-mono-plex text-xs tabular-nums ${
          isActive ? "text-[#FF2A2A]" : "text-[#A3A3A3]"
        }`}
      >
        {String(idx + 1).padStart(2, "0")}
      </span>
      <div className="min-w-0">
        <div
          className={`truncate font-display text-lg leading-tight ${
            isActive ? "text-[#F5F2E6]" : "text-[#F5F2E6]/90"
          }`}
        >
          {track.title}
        </div>
        <div className="truncate text-xs text-[#A3A3A3] font-mono-plex">
          {track.artist}
        </div>
      </div>
      <span className="font-mono-plex text-[10px] text-[#A3A3A3] tabular-nums">
        {track.duration}
      </span>
      <div className="w-7 flex justify-end">
        {isActive && <Equalizer playing />}
      </div>
    </li>
  );
};

const IntroColumn = () => (
  <div className="lg:col-span-5">
    <div className="font-mono-plex text-[11px] tracking-[0.3em] uppercase text-[#FFB800] flex items-center gap-3">
      <span className="h-px w-8 bg-[#FFB800]" /> The Jukebox
    </div>
    <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-[#F5F2E6]">
      HEAR THE
      <br />
      <span className="text-[#FF2A2A] neon-flicker">SUNNY'S SOUND.</span>
    </h2>
    <p className="mt-6 text-lg text-[#A3A3A3] max-w-md leading-relaxed">
      Texas country up front, classic rock in the back, somebody's ex's
      playlist at 1 AM. Tap a track to feel the room.
    </p>
    <div className="mt-6 inline-flex items-center gap-2 border border-[#262626] px-4 py-2 font-mono-plex text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
      <Music2 size={12} strokeWidth={2.5} className="text-[#FFB800]" />
      Playlist mock · swap-ready for the owner
    </div>
  </div>
);

const NowPlayingBar = ({ track, playing }) => (
  <div className="flex items-center gap-4 md:gap-5 p-5 md:p-6 border-b border-[#262626] bg-gradient-to-r from-[#1A1A1A] via-[#0A0A0A] to-[#1A1A1A]">
    <div className="relative w-16 h-16 md:w-20 md:h-20 shrink-0 bg-gradient-to-br from-[#FF2A2A] via-[#1A1A1A] to-[#FFB800] border border-[#262626] flex items-center justify-center">
      <Music2
        size={28}
        strokeWidth={2.5}
        className="text-[#F5F2E6] mix-blend-difference"
      />
      <span className="absolute inset-0 ring-1 ring-inset ring-white/10" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-mono-plex text-[9px] tracking-[0.3em] uppercase text-[#FFB800]">
        {playing ? "Now Spinning" : "Paused"}
      </div>
      <div className="mt-1 font-display text-2xl md:text-3xl text-[#F5F2E6] truncate">
        {track.title}
      </div>
      <div className="mt-0.5 text-sm text-[#A3A3A3] font-mono-plex truncate">
        {track.artist} · {track.duration}
      </div>
    </div>
    <Equalizer playing={playing} />
  </div>
);

const Controls = ({ playing, onPrev, onToggle, onNext }) => (
  <div className="flex items-center justify-center gap-4 p-4 border-b border-[#262626] bg-[#0A0A0A]">
    <button
      onClick={onPrev}
      data-testid="jukebox-prev"
      aria-label="Previous track"
      className="btn-press w-10 h-10 flex items-center justify-center border border-[#262626] text-[#A3A3A3] hover:text-[#F5F2E6] hover:border-[#FFB800]"
    >
      <SkipBack size={16} strokeWidth={2.5} />
    </button>
    <button
      onClick={onToggle}
      data-testid="jukebox-play"
      aria-label={playing ? "Pause" : "Play"}
      className="btn-press w-14 h-14 flex items-center justify-center bg-[#FFB800] text-black hover:bg-[#ffc433] rounded-full"
      style={{ boxShadow: "0 0 30px rgba(255, 184, 0, 0.35)" }}
    >
      {playing ? (
        <Pause size={20} strokeWidth={2.5} fill="black" />
      ) : (
        <Play size={20} strokeWidth={2.5} fill="black" />
      )}
    </button>
    <button
      onClick={onNext}
      data-testid="jukebox-next"
      aria-label="Next track"
      className="btn-press w-10 h-10 flex items-center justify-center border border-[#262626] text-[#A3A3A3] hover:text-[#F5F2E6] hover:border-[#FFB800]"
    >
      <SkipForward size={16} strokeWidth={2.5} />
    </button>
  </div>
);

const TrackList = ({ current, onSelect }) => (
  <ul data-testid="jukebox-tracklist" className="divide-y divide-[#1A1A1A]">
    {TRACKS.map((t, i) => (
      <TrackRow
        key={t.id}
        track={t}
        idx={i}
        current={current}
        onClick={onSelect}
      />
    ))}
  </ul>
);

const SpotifyJukebox = () => {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);

  const track = TRACKS[current];
  const next = () => setCurrent((c) => (c + 1) % TRACKS.length);
  const prev = () => setCurrent((c) => (c - 1 + TRACKS.length) % TRACKS.length);
  const togglePlay = () => setPlaying((p) => !p);
  const selectTrack = (idx) => {
    setCurrent(idx);
    setPlaying(true);
  };

  return (
    <section
      id="jukebox"
      data-testid="spotify-jukebox"
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#121212] border-y border-[#262626]"
    >
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <IntroColumn />
        <div className="lg:col-span-7">
          <div
            data-testid="jukebox-card"
            className="grain-card relative bg-[#0A0A0A] border border-[#262626] overflow-hidden"
          >
            <NowPlayingBar track={track} playing={playing} />
            <Controls
              playing={playing}
              onPrev={prev}
              onToggle={togglePlay}
              onNext={next}
            />
            <TrackList current={current} onSelect={selectTrack} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpotifyJukebox;
